import { useState, useEffect } from 'react'
import { Snowflake, Loader2, Check, Search, X } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { publicApi } from '@/lib/publicApi'

type Vendedor = { id: number; nome: string; codigo_vendedor: string | null }
type Cliente = { id: number; nome: string; codigo: string | null; documento: string | null }

export default function FreezerPreCadastroPage() {
  const [vendedores, setVendedores] = useState<Vendedor[]>([])
  const [vendedorId, setVendedorId] = useState<string>('')

  const [clienteSel, setClienteSel] = useState<Cliente | null>(null)
  const [clienteTexto, setClienteTexto] = useState('')
  const [resultados, setResultados] = useState<Cliente[]>([])
  const [buscando, setBuscando] = useState(false)

  const [patrimonio, setPatrimonio] = useState('')
  const [modelo, setModelo] = useState('')
  const [obs, setObs] = useState('')

  const [enviando, setEnviando] = useState(false)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  useEffect(() => {
    publicApi.get('/comodato/publico/vendedores').then(r => setVendedores(r.data)).catch(() => {})
  }, [])

  // busca de cliente (debounce)
  useEffect(() => {
    if (clienteSel) return
    const t = clienteTexto.trim()
    if (t.length < 2) { setResultados([]); return }
    setBuscando(true)
    const h = setTimeout(async () => {
      try {
        const r = await publicApi.get('/comodato/publico/clientes', { params: { q: t } })
        setResultados(r.data)
      } catch { setResultados([]) } finally { setBuscando(false) }
    }, 350)
    return () => clearTimeout(h)
  }, [clienteTexto, clienteSel])

  async function enviar() {
    const nome = clienteSel?.nome || clienteTexto.trim()
    if (!nome) { toast.error('Informe o cliente'); return }
    if (!patrimonio.trim()) { toast.error('Informe o número do patrimônio'); return }
    setEnviando(true)
    try {
      const vend = vendedores.find(v => String(v.id) === vendedorId)
      const r = await publicApi.post('/comodato/publico/pre-cadastro', {
        cliente_id: clienteSel?.id ?? null,
        cliente_nome: nome,
        cliente_codigo: clienteSel?.codigo ?? null,
        numero_patrimonio: patrimonio.trim(),
        modelo: modelo.trim() || null,
        vendedor_id: vend?.id ?? null,
        vendedor_nome: vend?.nome ?? null,
        observacao: obs.trim() || null,
      })
      setOkMsg(`Freezer ${r.data.numero_patrimonio} registrado em ${r.data.cliente_nome}`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar')
    } finally { setEnviando(false) }
  }

  function novoCadastro() {
    // mantém o vendedor; limpa o resto
    setOkMsg(null)
    setClienteSel(null); setClienteTexto(''); setResultados([])
    setPatrimonio(''); setModelo(''); setObs('')
  }

  if (okMsg) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-6 text-center">
        <Toaster position="top-center" />
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
          <Check size={44} className="text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Cadastrado!</h1>
        <p className="text-slate-600 mt-2 max-w-sm">{okMsg}</p>
        <button onClick={novoCadastro}
          className="mt-8 w-full max-w-sm bg-blue-600 text-white text-lg font-semibold rounded-xl py-4 active:bg-blue-700">
          Cadastrar outro freezer
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-center" />
      <div className="bg-blue-600 text-white px-5 py-4 flex items-center gap-2 sticky top-0 z-10">
        <Snowflake size={24} />
        <div>
          <h1 className="text-lg font-bold leading-tight">Cadastro de Freezer</h1>
          <p className="text-xs text-blue-100">Pré-cadastro do patrimônio no cliente</p>
        </div>
      </div>

      <div className="p-4 max-w-md mx-auto space-y-5 pb-28">
        {/* Vendedor */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Vendedor</label>
          <select value={vendedorId} onChange={e => setVendedorId(e.target.value)}
            className="w-full text-base rounded-xl border border-slate-300 px-4 py-3 bg-white">
            <option value="">Selecione seu nome...</option>
            {vendedores.map(v => (
              <option key={v.id} value={v.id}>{v.codigo_vendedor ? `${v.codigo_vendedor} — ` : ''}{v.nome}</option>
            ))}
          </select>
        </div>

        {/* Cliente */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Cliente</label>
          {clienteSel ? (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{clienteSel.nome}</div>
                <div className="text-xs text-slate-500">{clienteSel.codigo ? `Cód. ${clienteSel.codigo}` : 'sem código'}{clienteSel.documento ? ` · ${clienteSel.documento}` : ''}</div>
              </div>
              <button onClick={() => { setClienteSel(null); setClienteTexto('') }}
                className="text-slate-400 active:text-slate-600"><X size={20} /></button>
            </div>
          ) : (
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={clienteTexto} onChange={e => setClienteTexto(e.target.value)}
                placeholder="Nome, código ou CPF/CNPJ..."
                className="w-full text-base rounded-xl border border-slate-300 pl-10 pr-4 py-3 bg-white" />
              {buscando && <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
              {resultados.length > 0 && (
                <div className="mt-1 rounded-xl border border-slate-200 bg-white shadow divide-y max-h-64 overflow-auto">
                  {resultados.map(c => (
                    <button key={c.id} onClick={() => { setClienteSel(c); setResultados([]) }}
                      className="w-full text-left px-4 py-3 active:bg-blue-50">
                      <div className="font-medium text-slate-800 truncate">{c.nome}</div>
                      <div className="text-xs text-slate-500">{c.codigo ? `Cód. ${c.codigo}` : 'sem código'}{c.documento ? ` · ${c.documento}` : ''}</div>
                    </button>
                  ))}
                </div>
              )}
              {clienteTexto.trim().length >= 2 && !buscando && resultados.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">Não achei — pode deixar o nome digitado mesmo assim.</p>
              )}
            </div>
          )}
        </div>

        {/* Patrimônio */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Número do patrimônio</label>
          <input value={patrimonio} onChange={e => setPatrimonio(e.target.value)}
            inputMode="numeric" placeholder="Ex: 1234"
            className="w-full text-lg font-bold rounded-xl border border-slate-300 px-4 py-3 bg-white" />
        </div>

        {/* Modelo */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Modelo do freezer</label>
          <input value={modelo} onChange={e => setModelo(e.target.value)}
            placeholder="Ex: Metalfrio 400L"
            className="w-full text-base rounded-xl border border-slate-300 px-4 py-3 bg-white" />
        </div>

        {/* Obs */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Observação (opcional)</label>
          <input value={obs} onChange={e => setObs(e.target.value)}
            placeholder="Algo importante?"
            className="w-full text-base rounded-xl border border-slate-300 px-4 py-3 bg-white" />
        </div>
      </div>

      {/* Botão fixo */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          <button onClick={enviar} disabled={enviando}
            className="w-full bg-blue-600 text-white text-lg font-semibold rounded-xl py-4 active:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {enviando ? <><Loader2 size={20} className="animate-spin" /> Enviando...</> : <><Check size={20} /> Salvar cadastro</>}
          </button>
        </div>
      </div>
    </div>
  )
}
